import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Box, Button, Typography } from '@mui/material';
import { AvailableTimeWindow, TimeSlot } from '../../types/appointment';
import useTranslation from '../../hooks/useTranslation';
import moment, { Moment } from 'moment-timezone';

interface TimeInputProps {
  availableWindows: AvailableTimeWindow[];
  selectedDate: string | null;
  selectedTimeSlot: TimeSlot | null;
  onTimeSlotSelect: (timeSlot: TimeSlot | null) => void;
  timezone: string;
  duration: number;
}

const TimeInput: React.FC<TimeInputProps> = ({
  availableWindows,
  selectedDate,
  selectedTimeSlot,
  onTimeSlotSelect,
  timezone,
  duration,
}) => {
  const { t } = useTranslation();
  const [selectedStartTime, setSelectedStartTime] = useState<Moment | null>(
    null
  );
  const [selectedTimeSlotId, setSelectedTimeSlotId] = useState<string | null>(
    null
  );
  const prevTimezoneRef = useRef<string>(timezone);

  const handleTimeChange = (
    selectedTimeSlot: { id: string; moment: Moment } & TimeSlot
  ) => {
    if (!selectedTimeSlot || !selectedDate) {
      setSelectedStartTime(null);
      setSelectedTimeSlotId(null);
      return;
    }

    setSelectedTimeSlotId(selectedTimeSlot.id);

    // Use the moment object directly from the slot
    const combinedDateTime = selectedTimeSlot.moment.clone();

    setSelectedStartTime(combinedDateTime);

    const appointmentEnd = combinedDateTime.clone().add(duration, 'minutes');

    const timeSlot: TimeSlot = {
      id: selectedTimeSlot.id,
      startTime: combinedDateTime.toISOString(),
      endTime: appointmentEnd.toISOString(),
    };

    onTimeSlotSelect(timeSlot);
  };

  const formatEndTime = (): string => {
    if (!selectedStartTime) return '';

    const endTime = selectedStartTime.clone().add(duration, 'minutes');
    return endTime.format('HH:mm');
  };

  useEffect(() => {
    if (selectedTimeSlot) {
      const startTime = moment(selectedTimeSlot.startTime).tz(timezone);
      setSelectedStartTime(startTime);
      setSelectedTimeSlotId(selectedTimeSlot.id);
    } else {
      setSelectedStartTime(null);
      setSelectedTimeSlotId(null);
    }
  }, [selectedTimeSlot, timezone]);

  useEffect(() => {
    if (prevTimezoneRef.current !== timezone) {
      setSelectedStartTime(null);
      setSelectedTimeSlotId(null);
      if (selectedTimeSlot) {
        onTimeSlotSelect(null);
      }
      prevTimezoneRef.current = timezone;
    }
  }, [timezone, selectedTimeSlot, onTimeSlotSelect]);

  const generateTimeSlots = useMemo(() => {
    if (!selectedDate) return [];

    const dayWindows = availableWindows
      .filter(window => {
        const windowMoment = moment.utc(window.startTime).tz(timezone);
        const windowDateString = windowMoment.format('YYYY-MM-DD');
        return windowDateString === selectedDate;
      })
      .map(window => ({
        id: window.id,
        start: moment.utc(window.startTime).tz(timezone),
        end: moment.utc(window.endTime).tz(timezone),
      }));

    if (dayWindows.length === 0) return [];

    const slots: Array<
      {
        moment: Moment;
      } & TimeSlot
    > = [];

    dayWindows.forEach(window => {
      const slotStart = window.start.clone();
      let slotIndex = 0;

      while (slotStart.isBefore(window.end)) {
        const endTime = slotStart.clone().add(duration, 'minutes');
        if (endTime.isSameOrBefore(window.end)) {
          slots.push({
            id: `${window.id}-${slotIndex}`,
            moment: slotStart.clone(),
            startTime: slotStart.toISOString(),
            endTime: endTime.toISOString(),
          });
          slotIndex++;
        }
        slotStart.add(15, 'minutes');
      }
    });

    return slots;
  }, [availableWindows, selectedDate, timezone, duration]);

  if (!selectedDate) {
    return (
      <Box>
        <Typography variant="h6" fontWeight={600} sx={{ mb: 2 }}>
          {t('contact.booking.selectStartTime')}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {t('contact.booking.selectDateFirst')}
        </Typography>
      </Box>
    );
  }

  if (generateTimeSlots.length === 0) {
    return (
      <Box>
        <Typography variant="h6" fontWeight={600} sx={{ mb: 2 }}>
          {t('contact.booking.selectStartTime')}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {t('contact.booking.noSlotsAvailable')}
        </Typography>
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h6" fontWeight={600} sx={{ mb: 2 }}>
        {t('contact.booking.selectStartTime')} ({duration}{' '}
        {t('contact.booking.minutes')})
      </Typography>

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
          {generateTimeSlots.map(slot => {
            const isSelected = selectedTimeSlotId === slot.id;
            return (
              <Button
                key={slot.id}
                variant={isSelected ? 'contained' : 'outlined'}
                onClick={() => handleTimeChange(slot)}
                sx={{
                  py: 1.5,
                  px: 2,
                  borderRadius: 2,
                  textTransform: 'none',
                  fontSize: '0.875rem',
                  minWidth: '80px',
                }}
              >
                {slot.moment.format('HH:mm')}
              </Button>
            );
          })}
        </Box>

        {selectedStartTime && (
          <Box
            sx={{
              p: 2,
              bgcolor: 'background.paper',
              borderRadius: 2,
              border: 1,
              borderColor: 'divider',
            }}
          >
            <Typography variant="body1">
              <strong>{t('contact.booking.startTime')}:</strong>{' '}
              {selectedStartTime.format('HH:mm')}
            </Typography>
            <Typography variant="body1">
              <strong>{t('contact.booking.endTime')}:</strong> {formatEndTime()}
            </Typography>
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default TimeInput;
